/**
 * Reading approval_summary.validation_groups — the per-domain rollup MMP/VMP
 * persists (rules/summary.py::validation_groups).
 *
 * Shape, one entry per SOP domain that has at least one runnable rule:
 *
 *   { funding: { label: 'Funding',
 *                sop_sections: [...],
 *                rules: { 'FA-001': 'pass', 'FA-003': 'does_not_apply' },
 *                group_result: 'pass' } }
 *
 * There is deliberately no primary_status: the domain's rules are flat peers,
 * so group_result is the whole answer for the domain.
 *
 * Reading this beats the alternative the summary card uses for older programs,
 * which is to keyword-match a domain against the free text of key_findings —
 * that silently shows nothing when the agent rewords a finding, and can attach
 * one finding to two domains.
 */

/**
 * The seven outcome words, in the order that decides a group (worst first —
 * GROUP_PRECEDENCE in rules/summary.py), each with the Cloudscape
 * StatusIndicator type and the label to show a reviewer.
 *
 * `not_checked` is 'pending', not 'warning': it means the evidence never
 * reached the rule, which is the one result that on its own turns an otherwise
 * clean run into needs_review. `nothing_to_judge` is 'info' because it is a
 * conclusion, not a gap — collapsing the two is the mistake this table exists
 * to prevent.
 */
const GROUP_RESULT = {
  out_of_scope:     { status: 'warning', label: 'Out of Scope' },
  fail:             { status: 'error',   label: 'Fail' },
  needs_attention:  { status: 'warning', label: 'Needs Attention' },
  not_checked:      { status: 'pending', label: 'Not Checked' },
  pass:             { status: 'success', label: 'Pass' },
  nothing_to_judge: { status: 'info',    label: 'Nothing To Judge' },
  does_not_apply:   { status: 'info',    label: 'Does Not Apply' },
};

/** Human label for one outcome word ('needs_attention' → 'Needs Attention'). */
export function outcomeLabel(word) {
  const key = String(word ?? '');
  return GROUP_RESULT[key]?.label
    || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Describe one domain group for rendering.
 *
 * Returns null for a missing group so a caller can tell "this domain reported
 * nothing" apart from "this domain passed" — the agent omits a domain whose
 * rules are all unimplemented, and showing it as a pass would read as coverage
 * the run does not have.
 *
 * @param {object|null|undefined} group one value of validation_groups
 * @returns {{status: string, resultLabel: string, tally: Array<{word: string, label: string, count: number, rules: string[]}>}|null}
 */
export function describeValidationGroup(group) {
  if (!group || typeof group !== 'object') return null;
  const rules = group.rules && typeof group.rules === 'object' ? group.rules : {};
  const ruleIds = Object.keys(rules);
  const result = String(group.group_result ?? '');
  const known = GROUP_RESULT[result];

  // Tally in GROUP_RESULT order (worst first) so the reason a domain is not a
  // pass is the first thing read, and skip words no rule produced.
  const tally = Object.keys(GROUP_RESULT)
    .map((word) => {
      const matched = ruleIds.filter(id => rules[id] === word).sort();
      return { word, label: outcomeLabel(word), count: matched.length, rules: matched };
    })
    .filter(entry => entry.count > 0);

  return {
    status: known?.status || 'info',
    resultLabel: outcomeLabel(result),
    tally,
  };
}
